import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Actions
import { fetchOrders, fetchFiltersLabels, orderBulkActions } from '~/redux/actions/orders-actions';
import { CLEAR_ORDERS_FILTERS, SET_ORDERS_FILTERS } from '~/redux/actions/action-types';
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';
import {
  buildOrdersQueryParams,
  getOrdersFilterSize,
  parseOrdersQueryParams,
} from '~/utils/orderQueryParams';

// Component
import Table from '~/components/Orders/OrderListing';
import AdvanceFilters from './AdvanceFilters';
import NoOrdersScreen from './NoOrderScreen';
import CreateTestOrderConfirmModal from './CreateTestOrderConfirmModal';
import RescheduleOrderModal from '~/components/AdminPortal/Orders/RescheduleOrderModal';
import ConfirmModal from '../General/ConfirmModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Components
import { Container, Box, Grid, Typography, useMediaQuery, MenuItem } from '@mui/material';

// Icons
import {
  ExportNewCIcon,
  PayAndProcessNewCIcon,
  ProcessOrderNew,
  CancelOrderModalCIcon,
  CancelOrderNewCIcon,
  ExportModalNewCIcon,
} from '~/assets/images';

// styles
import './styles.scss';

const Orders = () => {
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const [openCreateConfirm, setOpenCreateConfirm] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [action, setAction] = useState({
    message: '',
    currentAction: null,
    successButtonName: '',
    loading: false,
  });
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const childRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ordersQueryString = searchParams.toString();
  const handledOrdersQueryRef = useRef(null);
  const lastOrdersRequestKeyRef = useRef(null);
  const previousMode = useRef(null);

  const currentMode = useSelector((state) =>
    state.userReducers.user.isLiveMode === true
      ? true
      : state.userReducers.user.isLiveMode === false
        ? false
        : null
  );

  const handleCreateOrder = () => {
    if (currentMode === true) {
      navigate('/orders/create-order');
    } else {
      setOpenCreateConfirm(true);
    }
  };

  const confirmCreateOrder = () => {
    setOpenCreateConfirm(false);
    navigate('/orders/create-order');
  };

  const totalOrdersInDB = useSelector((state) => state.ordersReducers.totalOrdersInDB);

  const filters = useSelector((state) => state.ordersReducers.filters);
  const filterSize = useSelector((state) => state.ordersReducers.filterSize);
  const isLoading = useSelector((state) => state.ordersReducers.loading);
  const ordersPerPage = useSelector(
    (state) => state.ordersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const ordersPerPageRef = useRef(ordersPerPage);
  const parsedOrdersQuery = useMemo(
    () => parseOrdersQueryParams(new URLSearchParams(ordersQueryString)),
    [ordersQueryString]
  );
  const appliedSearch = parsedOrdersQuery.search;
  // The column sort lives in the URL with search and filters, so it survives reload and back/forward.
  const { sort } = parsedOrdersQuery;
  const { filters: urlFilters } = parsedOrdersQuery;

  // Builds the next URL. Anything the caller doesn't pass is taken from the *current URL* (the source
  // of truth), never from Redux: Redux is only synced from the URL after a render, so reading it here
  // could write a stale filter/sort back and make the URL flip between two states.
  const updateOrdersUrl = useCallback(
    ({ search: nextSearch = search, filters: nextFilters = urlFilters, sort: nextSort = sort }) => {
      const nextSearchParams = new URLSearchParams();
      const nextQueryParams = buildOrdersQueryParams({
        search: nextSearch,
        filters: nextFilters,
        sort: nextSort,
      });

      Object.entries(nextQueryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => nextSearchParams.append(key, item));
          return;
        }
        nextSearchParams.set(key, value);
      });

      if (nextSearchParams.toString() === ordersQueryString) {
        return false;
      }

      setSearchParams(nextSearchParams, { replace: true });
      return true;
    },
    [ordersQueryString, search, setSearchParams, sort, urlFilters]
  );

  const handleSearch = () => {
    updateOrdersUrl({ search: search.trimStart() });
  };

  // A new sort goes to the URL; the URL effect below resets to page 1 and refetches.
  const handleSortChange = (nextSort) => {
    updateOrdersUrl({ search: appliedSearch, sort: nextSort });
  };

  // Emptying the input removes an applied search from the URL (the list then shows all orders again).
  const handleSearchChange = (event) => {
    const nextSearch = event.target.value.trimStart();

    setSearch(nextSearch);

    if (!nextSearch && appliedSearch) {
      updateOrdersUrl({ search: '' });
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const getOrders = useCallback(
    (overrides = {}) => {
      const nextSearch = overrides.search !== undefined ? overrides.search : appliedSearch;
      const nextFilters = overrides.filters !== undefined ? overrides.filters : filters;
      const nextApplyFilters =
        overrides.applyFilters !== undefined
          ? overrides.applyFilters
          : getOrdersFilterSize(nextFilters) > 0;
      const nextPagination = overrides.pagination || pagination;

      let payload = {
        pagination: nextPagination,
        search: nextSearch?.length ? nextSearch.trimStart() : null,
        ...sort,
        applyFilters: nextApplyFilters,
        filters: nextApplyFilters ? nextFilters : null,
      };
      dispatch(fetchOrders(payload));
    },
    [appliedSearch, dispatch, filters, pagination, sort]
  );

  const cancelOrders = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      setAction({ ...action, loading: true });
      const hasFilters = getOrdersFilterSize(filters) > 0;
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'cancel',
        search: appliedSearch.length ? appliedSearch : null,
        applyFilters: hasFilters,
        filters: hasFilters ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload));
      if (response?.status === 200) {
        getOrders();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 2000);
    }
  };

  const payAndProcessOrders = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      setAction({ ...action, loading: true });
      const hasFilters = getOrdersFilterSize(filters) > 0;
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'pay and process',
        search: appliedSearch.length ? appliedSearch : null,
        applyFilters: hasFilters,
        filters: hasFilters ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload));
      if (response?.status === 200) {
        getOrders();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 1000);
    }
  };

  const exportOrders = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      setAction({ ...action, loading: true });
      const hasFilters = getOrdersFilterSize(filters) > 0;
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'export',
        search: appliedSearch.length ? appliedSearch : null,
        applyFilters: hasFilters,
        filters: hasFilters ? filters : null,
      };
      const response = await dispatch(orderBulkActions(payload));
      if (response?.status === 200) {
        getOrders();
        setSelectedRows([]);
        dispatch(success(response.data?.message));
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
      }, 2000);
    }
  };

  const handleActions = async (type) => {
    if (type === 'cancel') {
      setOpenConfirmModal({
        type: 'cancel',
        open: true,
        message:
          selectedRows[0] === -1 || selectedRows.length > 1
            ? MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MULTI_MESSAGE
            : MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MESSAGE,
        subMessage: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MULTI_SUB_MESSAGE,
        handleOk: cancelOrders,
        header: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
        successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUCCESS_BUTTON,
        icon: CancelOrderModalCIcon,
        handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
      });
    } else if (type === 'payAndProcess') {
      setOpenConfirmModal({
        type: 'payAndProcess',
        open: true,
        message: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.MESSAGE,
        subMessage: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.MULTI_SUB_MESSAGE,
        handleOk: payAndProcessOrders,
        header: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.HEADING,
        successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUCCESS_BUTTON,
        icon: ProcessOrderNew,
        className: 'payAndProcessModal',
        handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
      });
    } else if (type === 'export') {
      setOpenConfirmModal({
        type: 'export',
        open: true,
        message:
          selectedRows[0] === -1 || selectedRows.length > 1
            ? MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.MULTI_MESSAGE
            : MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.MESSAGE,
        handleOk: exportOrders,
        header: MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.HEADING,
        successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.EXPORT.SUCCESS_BUTTON,
        icon: ExportModalNewCIcon,
        className: 'exportModalComponent',
        isExport: true,
        handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
      });
    }
  };

  useEffect(() => {
    setIsMounted(true);
    dispatch(fetchFiltersLabels());
    return () => {
      dispatch({ type: CLEAR_ORDERS_FILTERS });
    };
  }, [dispatch]);

  useEffect(() => {
    ordersPerPageRef.current = ordersPerPage;
  }, [ordersPerPage]);

  useEffect(() => {
    const {
      search: urlSearch,
      filters: urlFilters,
      applyFilters: urlApplyFilters,
      sort: urlSort,
    } = parsedOrdersQuery;
    const nextPagination = {
      page: DEFAULT_PAGINATION.page,
      pageSize: ordersPerPageRef.current,
    };

    setSearch(urlSearch);
    setSearchApplied(Boolean(urlSearch));
    setPagination(nextPagination);

    dispatch(
      urlApplyFilters
        ? {
            type: SET_ORDERS_FILTERS,
            payload: {
              applyFilters: true,
              filterSize: getOrdersFilterSize(urlFilters),
              filters: urlFilters,
            },
          }
        : { type: CLEAR_ORDERS_FILTERS }
    );

    if (handledOrdersQueryRef.current === ordersQueryString) {
      return;
    }

    const requestKey = `${ordersQueryString}|${nextPagination.page}|${nextPagination.pageSize}`;
    lastOrdersRequestKeyRef.current = requestKey;
    dispatch(
      fetchOrders({
        pagination: nextPagination,
        search: urlSearch.length ? urlSearch : null,
        ...urlSort,
        applyFilters: urlApplyFilters,
        filters: urlApplyFilters ? urlFilters : null,
      })
    );
    handledOrdersQueryRef.current = ordersQueryString;
  }, [dispatch, ordersQueryString, parsedOrdersQuery]);

  // Page / page-size changes fetch here. URL changes (search, filters, sort) are fetched by the URL
  // effect above, which also resets the page - so this effect must react to pagination only, otherwise
  // a URL change made on page 3 would fire a stale page-3 request before the reset to page 1 lands.
  const getOrdersRef = useRef(getOrders);
  getOrdersRef.current = getOrders;

  useEffect(() => {
    const requestKey = `${ordersQueryString}|${pagination.page}|${pagination.pageSize}`;
    if (lastOrdersRequestKeyRef.current === requestKey) {
      return;
    }

    if (isMounted) {
      lastOrdersRequestKeyRef.current = requestKey;
      getOrdersRef.current();
    }
    // ordersQueryString is read for the dedupe key only; it must not re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (openCreateConfirm === true && currentMode === true) {
      setOpenCreateConfirm(false);
      navigate('/orders/create-order');
    }
  }, [currentMode, navigate, openCreateConfirm]);

  useEffect(() => {
    if (currentMode === null) {
      return;
    }

    if (previousMode.current === null || previousMode.current === currentMode) {
      previousMode.current = currentMode;
      return;
    }

    previousMode.current = currentMode;
    const didUpdateUrl = updateOrdersUrl({ search: '', filters: {} });

    if (!didUpdateUrl) {
      const nextPagination = {
        page: DEFAULT_PAGINATION.page,
        pageSize: ordersPerPageRef.current,
      };

      setSearch('');
      setSearchApplied(false);
      handledOrdersQueryRef.current = '';
      setPagination(nextPagination);
      dispatch({ type: CLEAR_ORDERS_FILTERS });
      lastOrdersRequestKeyRef.current = [
        ordersQueryString,
        nextPagination.page,
        nextPagination.pageSize,
      ].join('|');
      dispatch(
        fetchOrders({
          pagination: nextPagination,
          search: null,
          ...sort,
          applyFilters: false,
          filters: null,
        })
      );
    }
  }, [currentMode, dispatch, ordersQueryString, sort, updateOrdersUrl]);

  const noOrdersInDb = () => {
    return (
      <Container maxWidth={maxWidth} className="olcEmptyStatePageContainer">
        <NoOrdersScreen
          handleCreateOrder={handleCreateOrder}
          openCreateConfirm={openCreateConfirm}
          setOpenCreateConfirm={setOpenCreateConfirm}
          confirmCreateOrder={confirmCreateOrder}
        />
      </Container>
    );
  };

  if (!isLoading && totalOrdersInDB < 1 && totalOrdersInDB !== null) {
    return noOrdersInDb();
  }

  return (
    <Container maxWidth={maxWidth} className="ordersPageContainer olcPrimaryListPage">
      <Grid container columnSpacing={1} className="ordersToolbarRow">
        <Grid
          item
          lg={8}
          md={filterSize > 0 && selectedRows.length > 0 ? 8 : 6}
          sm={12}
          xs={12}
          className="searchBtnWrapper newSearchBtnWrapper"
        >
          <Box className="searchWrapper">
            <OlcSearchInput
              searchApplied={searchApplied}
              sx={{
                '@media (max-width: 900px)': {
                  maxWidth:
                    filterSize > 0 && selectedRows.length > 0 ? '100% !important' : 'inherit',
                },
              }}
              type="text"
              value={search}
              onChange={handleSearchChange}
              onKeyDown={debouncedHandleKeyDown}
              placeholder={MESSAGES.ORDERS.SEARCH_PLACE_HOLDER}
              title={MESSAGES.ORDERS.SEARCH_PLACE_HOLDER}
              onClear={() => {
                updateOrdersUrl({ search: '' });
              }}
            />
            <Box className="filterBtnWrapper">
              <OlcButton
                variant="secondary"
                size="small"
                onClick={() => {
                  setOpenAdvanceFilters(true);
                }}
              >
                Filters
              </OlcButton>
              {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
            </Box>
            {filterSize > 0 && (
              <OlcButton
                variant="secondary"
                onClick={() => {
                  childRef.current.clearFilters();
                }}
                className="clearFilterBtn"
              >
                {MESSAGES.ORDERS.CLEAR_FILTER_BTN_TEXT}
              </OlcButton>
            )}
          </Box>
        </Grid>
        <Grid
          item
          lg={4}
          md={filterSize > 0 && selectedRows.length > 0 ? 4 : 6}
          sm={12}
          xs={12}
          className="ordersActionsGrid"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          {/* Create Button */}
          <Box className="actionsBtnWrapperOrders">
            {selectedRows.length > 0 && (
              <OlcButton
                variant="secondary"
                size="small"
                dropdown
                expanded={open}
                onClick={handleClick}
              >
                Actions
              </OlcButton>
            )}
            <OlcButton variant="primary" size="small" onClick={handleCreateOrder}>
              Create
            </OlcButton>
          </Box>
        </Grid>
      </Grid>
      <Table
        pagination={pagination}
        updatePagination={setPagination}
        sort={sort}
        onSortChange={handleSortChange}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        searchApplied={searchApplied}
        setRescheduleModalOpen={setRescheduleModalOpen}
        setSelectedOrder={setSelectedOrder}
        setActiveRow={setActiveRow}
        getOrders={getOrders}
        totalOrdersInDB={totalOrdersInDB}
        filterSize={filterSize}
      />
      <AdvanceFilters
        open={openAdvanceFilters}
        handleCloseFilter={() => {
          setOpenAdvanceFilters(false);
        }}
        ref={childRef}
        updateOrdersUrl={updateOrdersUrl}
        currentSearch={search}
      />

      {/* Confirm Modal Generic */}
      <ConfirmModal openConfirmModal={openConfirmModal} setOpenConfirmModal={setOpenConfirmModal} />

      <CreateTestOrderConfirmModal
        open={openCreateConfirm}
        onClose={() => setOpenCreateConfirm(false)}
        onConfirm={confirmCreateOrder}
      />

      <RescheduleOrderModal
        openModal={rescheduleModalOpen}
        handleCloseModal={() => setRescheduleModalOpen(false)}
        selectedOrder={selectedOrder}
        getOrders={getOrders}
        activeRow={activeRow}
      />
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          marginTop: '4px',
        }}
      >
        <MenuItem onClick={() => handleActions('payAndProcess')}>
          <img src={PayAndProcessNewCIcon} alt="pay and process" />
          Pay and Process
        </MenuItem>
        <MenuItem onClick={() => handleActions('cancel')}>
          <img src={CancelOrderNewCIcon} alt="cancel" />
          Cancel
        </MenuItem>
        <MenuItem onClick={() => handleActions('export')}>
          <img src={ExportNewCIcon} alt="export" />
          Export Orders
        </MenuItem>
      </OlcActionMenu>
    </Container>
  );
};

export default Orders;
